import { styled } from '@linaria/react'
import React, { FC, useCallback, useRef } from 'react'
import { colors } from '../styles/colors'
import { Typography } from './Typography'

interface PercentageSliderProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  className?: string
}

export const PercentageSlider: FC<PercentageSliderProps> = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  className,
}) => {
  const sliderRef = useRef<HTMLDivElement>(null)

  const updateValue = useCallback(
    (clientX: number) => {
      if (!sliderRef.current) return

      const rect = sliderRef.current.getBoundingClientRect()
      const percentage = Math.max(
        0,
        Math.min(1, (clientX - rect.left) / rect.width),
      )
      const newValue =
        Math.round((min + percentage * (max - min)) / step) * step
      onChange(Math.max(min, Math.min(max, newValue)))
    },
    [min, max, step, onChange],
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (disabled || !sliderRef.current) return

      const handleMouseMove = (e: MouseEvent) => {
        updateValue(e.clientX)
      }

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      updateValue(e.clientX)
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [disabled, updateValue],
  )

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (disabled || !sliderRef.current) return

      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault()
        if (e.touches.length > 0) {
          updateValue(e.touches[0].clientX)
        }
      }

      const handleTouchEnd = () => {
        document.removeEventListener('touchmove', handleTouchMove)
        document.removeEventListener('touchend', handleTouchEnd)
      }

      if (e.touches.length > 0) {
        updateValue(e.touches[0].clientX)
      }
      document.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      })
      document.addEventListener('touchend', handleTouchEnd)
    },
    [disabled, updateValue],
  )

  const percentage = ((value - min) / (max - min)) * 100

  return (
    <SliderContainer className={className}>
      <SliderTrack
        ref={sliderRef}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        disabled={disabled}
      >
        <SliderProgress style={{ width: `${percentage}%` }} />
        <SliderThumb style={{ left: `${percentage}%` }} disabled={disabled} />
      </SliderTrack>

      <ValueDisplay>
        <Typography fontSize={14} fontWeight={400} font="roboto">
          {value}%
        </Typography>
      </ValueDisplay>
    </SliderContainer>
  )
}

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`

const SliderTrack = styled.div<{ disabled: boolean }>`
  position: relative;
  flex: 1;
  height: 4px;
  background: ${colors.grey100};
  border-radius: 8px;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
`

const SliderProgress = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: ${colors.secondary};
  border-radius: 8px;
  transition: width 0.1s ease;
`

const SliderThumb = styled.div<{ disabled: boolean }>`
  position: absolute;
  top: 50%;
  width: 20px;
  height: 20px;
  background: ${colors.white};
  border: 2px solid ${colors.secondary};
  border-radius: 50%;
  transform: translate(-50%, -50%);
  cursor: ${props => (props.disabled ? 'not-allowed' : 'grab')};
  box-shadow: 0px 2px 4px ${colors.shadowLight};
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0px 4px 8px ${colors.shadowLight};
  }

  &:active {
    cursor: grabbing;
    box-shadow: 0px 4px 12px ${colors.shadowMedium};
  }
`

const ValueDisplay = styled.div`
  background: ${colors.grey100};
  border: 1px solid ${colors.grey300};
  border-radius: 12px;
  padding: 13px 12px;
  min-width: 60px;
  text-align: center;
`
